import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export const useQueryParams = <T extends Record<string, any>>({
  initialData,
}: {
  isMainScreen?: boolean;
  initialData?: T;
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitialized = useRef(false);

  // ✅ Use initialData directly if searchParams is empty
  const currentParams =
    searchParams.size === 0 && initialData
      ? { ...initialData }
      : Object.fromEntries([...searchParams.entries()]);

  // Merge currentParams with initialData
  const safeParams: Record<string, any> = {};
  const allKeys = new Set([
    ...Object.keys(initialData || {}),
    ...Object.keys(currentParams),
  ]);

  for (const key of allKeys) {
    const value = currentParams[key];
    if (value !== undefined) {
      safeParams[key] = value === 'undefined' ? undefined : value;
    } else {
      safeParams[key] = initialData?.[key];
    }
  }

  // Parse numeric values (e.g., page and limit)
  const parsedPage = Number(currentParams.page);
  safeParams.page =
    Number.isInteger(parsedPage) && parsedPage > 0
      ? parsedPage
      : initialData?.page || 1;

  const parsedLimit = Number(currentParams.limit);
  safeParams.limit =
    Number.isInteger(parsedLimit) && parsedLimit > 0
      ? parsedLimit
      : initialData?.limit || 10;

  const queryhash = JSON.stringify(safeParams);

  // Initialize the URL once from initialData
  useEffect(() => {
    if (initialData && !hasInitialized.current) {
      const sanitizedInitialData = Object.entries(initialData).reduce(
        (acc, [key, val]) => {
          if (val !== undefined && val !== null) {
            acc[key] = String(val);
          }
          return acc;
        },
        {} as Record<string, string>
      );
      setSearchParams(sanitizedInitialData);
      hasInitialized.current = true;
    }
  }, [initialData, setSearchParams]);

  // Utilities
  const setQuery = (params: Partial<T>) => {
    const updated = { ...safeParams, ...params };
    const sanitized = Object.entries(updated).reduce(
      (acc, [key, val]) => {
        if (val !== undefined && val !== null) {
          acc[key] = String(val);
        }
        return acc;
      },
      {} as Record<string, string>
    );
    setSearchParams(sanitized);
  };

  const updateQuery = (key: string, value: string) => {
    const updated = { ...safeParams, [key]: value };
    const sanitized = Object.entries(updated).reduce(
      (acc, [k, v]) => {
        if (v !== undefined && v !== null) {
          acc[k] = String(v);
        }
        return acc;
      },
      {} as Record<string, string>
    );
    setSearchParams(sanitized);
  };

  const setFilters = (newFilters: Partial<T>) => {
    const updated = { ...safeParams, ...newFilters };
    const sanitized = Object.entries(updated).reduce(
      (acc, [k, v]) => {
        if (v !== undefined && v !== null) {
          acc[k] = String(v);
        }
        return acc;
      },
      {} as Record<string, string>
    );
    setSearchParams(sanitized);
  };

  return {
    query: safeParams as T,
    queryhash,
    setQuery,
    updateQuery,
    filters: safeParams as T,
    setFilters,
  };
};
